// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — PierBazaar.js  (THE BOARDWALK BAZAAR)
// ────────────────────────────────────────────────────────────────────
// The storefront over bazaar.js. Full-screen overlay, same modal
// pattern as the Crest Well. Three counters under one canvas roof:
//
//   BROWSE   — every open listing, by category. Inspect → buy.
//   MY STALL — mint new editions + manage your open listings.
//   GALLERY  — every piece you own, its Sigil Code, its provenance
//              radiance, and a LIST FOR SALE flow.
//
// Usage from UnstablePierre:
//   {showBazaar && (
//     <PierBazaar uid={uid} playerName={name} onClose={() => setShowBazaar(false)} />
//   )}
//
// Kept by a new Willie — the Bazaar broker (name pending caP'n's
// ruling; placeholder "Wyllie" throughout, one constant to change).
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from "react";
import { SD } from "./theme";
import {
  listenToProfile, listenToRates, ensureRateTable,
  CURRENCIES, DC_ID, tintFor,
} from "./progression";
import {
  BAZAAR_CATEGORIES, bazaarCurrencies, priceBoundsFor,
  MIN_EDITION_SIZE, MAX_EDITION_SIZE,
  PIECE_PROVENANCE, APPROVED_IMAGE_HOSTS,
  mintEdition, listPiece, delistPiece, buyPiece,
  listenToOpenListings, listenToMyListings, listenToGallery,
  getMint, isApprovedImage,
  listenToLedger, listenToCreatorMints,
} from "./bazaar";

const BROKER_NAME = "Wyllie"; // ← pending caP'n's Willie-spelling ruling

// ── Keyframes — head injection, id-guarded (the keyframe law) ────────
const BZ_KEYFRAMES = `
@keyframes bz-pulse { 0%,100% { opacity:.45; } 50% { opacity:.95; } }
@keyframes bz-spin  { 0% { transform:rotate(0deg);} 100% { transform:rotate(360deg);} }
@keyframes bz-in    { 0% { opacity:0; transform:translateY(14px);} 100% { opacity:1; transform:none;} }
@media (prefers-reduced-motion: reduce) {
  .bz-anim { animation: none !important; }
}`;
const BZ_STYLE_ID = "bz-keyframes";
function ensureBazaarKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(BZ_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = BZ_STYLE_ID;
  el.textContent = BZ_KEYFRAMES;
  document.head.appendChild(el);
}
ensureBazaarKeyframes();

// ── Small shared bits ────────────────────────────────────────────────
// Reads the CURRENCIES registry directly rather than the rate table, so
// a price still renders correctly even for a currency that has since
// been deactivated at the Well. A ledger row from last month must not
// turn into a blank glyph because caP'n retired a currency yesterday.
const Price = ({ price, currency, size = 15 }) => {
  const meta = CURRENCIES[currency] || CURRENCIES[DC_ID];
  return (
    <span style={{
      color: tintFor(currency), fontWeight: 900,
      fontSize: size, letterSpacing: 0.5,
    }}>
      {meta.icon} {Number(price).toLocaleString()}
    </span>
  );
};

// Provenance mark — earned glows brighter than bought (the Provenance Law).
const ProvenanceMark = ({ provenance, size = 10 }) => {
  const p = PIECE_PROVENANCE[provenance] || PIECE_PROVENANCE.bazaar;
  return (
    <span
      className={p.radiance >= 2 ? "bz-anim" : ""}
      title={p.label}
      style={{
        fontSize: size,
        fontWeight: 900,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: p.color,
        textShadow: p.radiance >= 2 ? `0 0 8px ${p.color}` : "none",
        animation: p.radiance >= 3 ? "bz-pulse 2.4s ease-in-out infinite" : "none",
      }}
    >
      {p.label}
    </span>
  );
};

const SigilCode = ({ code }) => (
  <span
    style={{
      fontFamily: SD.fontMono,
      fontSize: 10,
      color: SD.goldBright,
      background: "#00000066",
      border: `1px solid ${SD.goldDim}`,
      borderRadius: 4,
      padding: "2px 6px",
      letterSpacing: 0.5,
      whiteSpace: "nowrap",
    }}
  >
    {code}
  </span>
);

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  background: SD.void2,
  border: `1px solid ${SD.goldDim}`,
  borderRadius: 6,
  color: SD.starWhite,
  fontFamily: SD.fontMono,
  fontSize: 13,
  padding: "8px 10px",
  outline: "none",
};

const btnStyle = (color = SD.gold, filled = false) => ({
  background: filled ? color : "transparent",
  color: filled ? SD.void1 : color,
  border: `1px solid ${color}`,
  borderRadius: 6,
  fontFamily: SD.fontDisplay,
  fontWeight: 900,
  fontSize: 12,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  padding: "8px 14px",
  cursor: "pointer",
});

// A minimal inline text-button for tappable names (creator/collector links).
const linkBtn = (color) => ({
  background: "none", border: "none", padding: 0, margin: 0,
  color, fontWeight: 900, fontFamily: SD.fontMono, fontSize: 12,
  cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2,
});

// ── Listing card (BROWSE + MY STALL share it) ────────────────────────
function ListingCard({ listing, mine, busy, onBuy, onDelist, onOpenCreator }) {
  const copy = listing.piece?.copyNo;
  const canOpenCreator = !!(listing.creator && onOpenCreator);
  return (
    <div
      className="bz-anim"
      style={{
        animation: "bz-in .3s ease both",
        background: SD.void3,
        border: `1px solid ${mine ? SD.purpleDim : SD.goldDim}`,
        borderRadius: 10,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ position: "relative", paddingTop: "72%", background: SD.void1 }}>
        <img
          src={listing.image}
          alt={listing.title}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover",
          }}
        />
        <div
          style={{
            position: "absolute", top: 6, right: 6,
            background: "#000000aa", borderRadius: 5, padding: "2px 7px",
            fontSize: 10, fontWeight: 900, color: SD.starSilver, letterSpacing: 1,
          }}
        >
          {copy ? `${copy}/${listing.editionSize}` : `ED. ${listing.editionSize}`}
        </div>
      </div>

      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
        <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, fontSize: 14, color: SD.starWhite }}>
          {listing.title}
        </div>
        <div style={{ fontSize: 11, color: SD.starDim }}>
          by {canOpenCreator ? (
            <button
              onClick={() => onOpenCreator(listing.creator, listing.creatorName)}
              style={linkBtn(SD.purpleLight)}
              title="See this creator's work"
            >{listing.creatorName}</button>
          ) : (
            <span style={{ color: SD.purpleLight, fontWeight: 700 }}>{listing.creatorName}</span>
          )}
          {" · "}sold by <span style={{ color: SD.starSilver }}>{listing.sellerName}</span>
        </div>
        {listing.piece?.code && <SigilCode code={listing.piece.code} />}

        <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, paddingTop: 6 }}>
          <Price price={listing.price} currency={listing.currency} />
          {mine ? (
            <button style={btnStyle(SD.red)} disabled={busy} onClick={() => onDelist(listing)}>
              Delist
            </button>
          ) : (
            <button style={btnStyle(SD.goldBright, true)} disabled={busy} onClick={() => onBuy(listing)}>
              Buy
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── THE BAZAAR ───────────────────────────────────────────────────────
export default function PierBazaar({ uid, playerName, onClose }) {
  const [tab, setTab] = useState("browse"); // browse | stall | gallery
  const [category, setCategory] = useState("art");

  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [pulse, setPulse] = useState([]);           // live sales feed (the Market Pulse)

  // Creator gallery overlay — { uid, name } when open, else null.
  const [creatorView, setCreatorView] = useState(null);

  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null); // { ok, msg }
  const [confirmBuy, setConfirmBuy] = useState(null); // listing

  // Mint form
  const [mTitle, setMTitle] = useState("");
  const [mDesc, setMDesc] = useState("");
  const [mImage, setMImage] = useState("");
  const [mSize, setMSize] = useState(10);

  // List-for-sale form (from gallery)
  const [selling, setSelling] = useState(null); // piece
  const [sPrice, setSPrice] = useState(10);
  const [sCurrency, setSCurrency] = useState("DC");
  const [sellingMint, setSellingMint] = useState(null);

  useEffect(() => {
    const offs = [
      listenToProfile(uid, setProfile),
      listenToOpenListings(setListings),
      listenToMyListings(uid, setMyListings),
      listenToGallery(uid, setGallery),
      listenToLedger(setPulse, 30),
    ];
    return () => offs.forEach((off) => off && off());
  }, [uid]);

  useEffect(() => {
    let alive = true;
    if (selling?.artId && !selling.printId) {
      getMint(selling.artId).then((m) => alive && setSellingMint(m));
    } else {
      setSellingMint(null);
    }
    return () => { alive = false; };
  }, [selling]);

  const flash = (ok, msg) => {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 3500);
  };

  // Open a creator's gallery overlay ("everything by this creator").
  const openCreator = (creatorUid, creatorName) => {
    if (!creatorUid) return;
    setCreatorView({ uid: creatorUid, name: creatorName || "A Creator" });
  };

  const openListings = useMemo(
    () => listings.filter((l) => l.category === category && l.seller !== uid),
    [listings, category, uid]
  );
  const myOpenListings = useMemo(
    () => myListings.filter((l) => l.status === "open"),
    [myListings]
  );

  // ── Live rate table ────────────────────────────────────────────────
  // Drives which currencies a seller may price in. Seeded once if the
  // table is absent, then streamed — so a new title's coin shows up the
  // moment caP'n adds its row in the console.
  const [rates, setRates] = useState({});
  useEffect(() => {
    let unsub;
    ensureRateTable().finally(() => { unsub = listenToRates(setRates); });
    return () => { if (typeof unsub === "function") unsub(); };
  }, []);

  const sellableCurrencies = useMemo(() => bazaarCurrencies(rates), [rates]);

  // Every registered currency the player holds, not just DC and SE.
  const wallet = useMemo(() => {
    const out = {};
    for (const [id, meta] of Object.entries(CURRENCIES)) {
      out[id] = profile?.[meta.path] || 0;
    }
    return out;
  }, [profile]);

  // ── Actions ────────────────────────────────────────────────────────
  const doBuy = async (listing) => {
    setBusy(true);
    const res = await buyPiece(uid, playerName, listing.listingId);
    setBusy(false);
    setConfirmBuy(null);
    flash(res.success, res.success
      ? `Yours. ${CURRENCIES[res.currency]?.icon || ""} ${res.price.toLocaleString()} paid — it's in your Gallery.`
      : res.reason);
  };

  const doDelist = async (listing) => {
    setBusy(true);
    const res = await delistPiece(uid, listing.listingId);
    setBusy(false);
    flash(res.success, res.success ? "Returned to your Gallery." : res.reason);
  };

  const doMint = async () => {
    setBusy(true);
    const res = await mintEdition(uid, playerName, {
      title: mTitle, desc: mDesc, image: mImage,
      category: "art", editionSize: mSize,
    });
    setBusy(false);
    if (res.success) {
      flash(true, `Minted ${res.editionSize}× "${mTitle.trim()}" — serial ${res.serial}. All copies are in your Gallery.`);
      setMTitle(""); setMDesc(""); setMImage(""); setMSize(10);
      setTab("gallery");
    } else {
      flash(false, res.reason);
    }
  };

  const doList = async () => {
    if (!selling) return;
    setBusy(true);
    const res = await listPiece(uid, playerName, selling.pieceId, sPrice, sCurrency);
    setBusy(false);
    if (res.success) {
      flash(true, "Listed. Your stall is open.");
      setSelling(null);
      setTab("stall");
    } else {
      flash(false, res.reason);
    }
  };

  // ── Frame ──────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9500,
        background: "#000000cc",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 12,
      }}
    >
      <div
        style={{
          width: "min(960px, 100%)",
          height: "min(680px, 100%)",
          background: SD.void2,
          border: `2px solid ${SD.gold}`,
          borderRadius: 14,
          boxShadow: `0 0 40px ${SD.gold}33`,
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          fontFamily: SD.fontMono,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 16px",
            borderBottom: `1px solid ${SD.goldDim}`,
            background: SD.void3,
          }}
        >
          <div style={{ fontSize: 22 }}>🎪</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, fontSize: 17, color: SD.goldBright, letterSpacing: 2 }}>
              THE BOARDWALK BAZAAR
            </div>
            <div style={{ fontSize: 10, color: SD.starDim, letterSpacing: 1 }}>
              {BROKER_NAME}'s counter · every trade witnessed · the seller keeps every coin
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            {Object.keys(CURRENCIES)
              .filter((id) => id === DC_ID || wallet[id] > 0)
              .map((id) => (
                <Price key={id} price={wallet[id]} currency={id} size={13} />
              ))}
          </div>
          <button style={btnStyle(SD.starSilver)} onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: `1px solid ${SD.goldDim}`, background: SD.void3 }}>
          {[
            ["browse", "Browse"],
            ["pulse", "Pulse"],
            ["stall", `My Stall (${myOpenListings.length})`],
            ["gallery", `Gallery (${gallery.length})`],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                flex: 1, padding: "10px 0",
                background: tab === id ? SD.void2 : "transparent",
                border: "none",
                borderBottom: tab === id ? `2px solid ${SD.goldBright}` : "2px solid transparent",
                color: tab === id ? SD.goldBright : SD.starDim,
                fontFamily: SD.fontDisplay, fontWeight: 900, fontSize: 13,
                letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {/* ── BROWSE ── */}
          {tab === "browse" && (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                {Object.entries(BAZAAR_CATEGORIES).map(([id, c]) => (
                  <button
                    key={id}
                    disabled={!c.live}
                    onClick={() => c.live && setCategory(id)}
                    title={c.live ? c.label : `${c.label} — coming soon`}
                    style={{
                      ...btnStyle(category === id ? SD.goldBright : SD.starDim, category === id),
                      opacity: c.live ? 1 : 0.4,
                      cursor: c.live ? "pointer" : "not-allowed",
                    }}
                  >
                    {c.icon} {c.label}{!c.live && " · SOON"}
                  </button>
                ))}
              </div>

              {openListings.length === 0 ? (
                <div style={{ textAlign: "center", color: SD.starDim, padding: "60px 20px", fontSize: 13 }}>
                  The stalls are quiet. Mint something in <b>My Stall</b> and be the first name on the boards.
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                  {openListings.map((l) => (
                    <ListingCard
                      key={l.listingId}
                      listing={l}
                      mine={false}
                      busy={busy}
                      onBuy={setConfirmBuy}
                      onDelist={() => {}}
                      onOpenCreator={openCreator}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── PULSE — the living market feed ── */}
          {tab === "pulse" && (
            <MarketPulse pulse={pulse} onOpenCreator={openCreator} />
          )}

          {/* ── MY STALL ── */}
          {tab === "stall" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Mint form */}
              <div style={{ background: SD.void3, border: `1px solid ${SD.goldDim}`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, color: SD.goldBright, letterSpacing: 2, fontSize: 13, marginBottom: 4 }}>
                  MINT A NEW EDITION
                </div>
                <div style={{ fontSize: 10, color: SD.starDim, marginBottom: 10, lineHeight: 1.5 }}>
                  Every copy gets its own Sigil Code — provably yours, provably numbered. Edition size is
                  <b> forever</b>: you can never print more of this piece. (v1 art must live on the IE CDN:{" "}
                  {APPROVED_IMAGE_HOSTS.join(", ")}. Player uploads arrive in a later phase.)
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <input style={inputStyle} placeholder="Title (2–60 chars)" value={mTitle} maxLength={60}
                    onChange={(e) => setMTitle(e.target.value)} />
                  <input style={inputStyle} type="number" min={MIN_EDITION_SIZE} max={MAX_EDITION_SIZE}
                    placeholder={`Edition size (${MIN_EDITION_SIZE}–${MAX_EDITION_SIZE})`}
                    value={mSize} onChange={(e) => setMSize(e.target.value)} />
                  <input style={{ ...inputStyle, gridColumn: "1 / -1" }} placeholder="Image URL (https://terror-assets.pages.dev/...)"
                    value={mImage} onChange={(e) => setMImage(e.target.value)} />
                  <input style={{ ...inputStyle, gridColumn: "1 / -1" }} placeholder="Description (optional, 300 max)"
                    value={mDesc} maxLength={300} onChange={(e) => setMDesc(e.target.value)} />
                </div>
                {mImage && !isApprovedImage(mImage) && (
                  <div style={{ fontSize: 10, color: SD.red, marginTop: 6 }}>
                    That URL isn't on the IE CDN — the mint will be refused.
                  </div>
                )}
                <div style={{ marginTop: 10, textAlign: "right" }}>
                  <button style={btnStyle(SD.goldBright, true)} disabled={busy} onClick={doMint}>
                    ◆ Mint Edition
                  </button>
                </div>
              </div>

              {/* Open listings */}
              <div>
                <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, color: SD.starSilver, letterSpacing: 2, fontSize: 12, marginBottom: 8 }}>
                  YOUR OPEN LISTINGS
                </div>
                {myOpenListings.length === 0 ? (
                  <div style={{ color: SD.starDim, fontSize: 12 }}>
                    Nothing on the boards. List a piece from your Gallery.
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                    {myOpenListings.map((l) => (
                      <ListingCard key={l.listingId} listing={l} mine busy={busy} onBuy={() => {}} onDelist={doDelist} onOpenCreator={openCreator} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── GALLERY ── */}
          {tab === "gallery" && (
            gallery.length === 0 ? (
              <div style={{ textAlign: "center", color: SD.starDim, padding: "60px 20px", fontSize: 13 }}>
                Your gallery is empty. Mint an edition, buy from the boards, or earn a print through deeds.
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
                {gallery.map((p) => (
                  <GalleryPiece key={p.pieceId} piece={p} onSell={() => { setSelling(p); setSPrice(10); setSCurrency("DC"); }} />
                ))}
              </div>
            )
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div
            style={{
              position: "absolute", bottom: 18, left: "50%", transform: "translateX(-50%)",
              background: toast.ok ? SD.void3 : SD.redDim,
              border: `1px solid ${toast.ok ? SD.goldBright : SD.red}`,
              color: SD.starWhite, borderRadius: 8, padding: "10px 18px",
              fontSize: 12, maxWidth: "80%", textAlign: "center", zIndex: 5,
            }}
          >
            {toast.msg}
          </div>
        )}
      </div>

      {/* Buy confirm */}
      {confirmBuy && (
        <Modal onClose={() => setConfirmBuy(null)}>
          <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, fontSize: 15, color: SD.goldBright, marginBottom: 8 }}>
            Buy "{confirmBuy.title}"?
          </div>
          <div style={{ fontSize: 12, color: SD.starSilver, lineHeight: 1.6, marginBottom: 6 }}>
            Copy <b>{confirmBuy.piece?.copyNo}/{confirmBuy.editionSize}</b> by{" "}
            <span style={{ color: SD.purpleLight }}>{confirmBuy.creatorName}</span>, sold by{" "}
            {confirmBuy.sellerName}.
          </div>
          {confirmBuy.piece?.code && <SigilCode code={confirmBuy.piece.code} />}
          <div style={{ margin: "12px 0", fontSize: 14 }}>
            <Price price={confirmBuy.price} currency={confirmBuy.currency} size={16} />
            <span style={{ fontSize: 10, color: SD.starDim, marginLeft: 8 }}>
              (every coin of it goes to {confirmBuy.sellerName})
            </span>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={btnStyle(SD.starSilver)} onClick={() => setConfirmBuy(null)}>Walk away</button>
            <button style={btnStyle(SD.goldBright, true)} disabled={busy} onClick={() => doBuy(confirmBuy)}>
              Pay & Take
            </button>
          </div>
        </Modal>
      )}

      {/* List-for-sale */}
      {selling && (
        <Modal onClose={() => setSelling(null)}>
          <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, fontSize: 15, color: SD.goldBright, marginBottom: 8 }}>
            List "{sellingMint?.title || selling.printId || selling.artId}"
          </div>
          <div style={{ marginBottom: 10 }}><SigilCode code={selling.code} /></div>
          <div style={{ fontSize: 10, color: SD.starDim, marginBottom: 10, lineHeight: 1.5 }}>
            Listing moves the piece into escrow — it leaves your Gallery until it sells or you delist.
            When it sells, you keep the full asking price. Pierre takes nothing.
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              type="number"
              min={priceBoundsFor(sCurrency, rates).min}
              max={priceBoundsFor(sCurrency, rates).max}
              value={sPrice}
              onChange={(e) => setSPrice(e.target.value)}
            />
            {/* Every currency registered at the Crest Well is legal
                tender here. A new title's coin appears the day its rate
                row goes live — no code change, no deploy. */}
            {Object.entries(sellableCurrencies).map(([id, c]) => (
              <button
                key={id}
                onClick={() => setSCurrency(id)}
                style={btnStyle(sCurrency === id ? c.color : SD.starDim, sCurrency === id)}
                title={c.provisional ? `${c.label} — rate is provisional` : c.label}
              >
                {c.glyph} {id}{c.provisional ? "*" : ""}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={btnStyle(SD.starSilver)} onClick={() => setSelling(null)}>Cancel</button>
            <button style={btnStyle(SD.goldBright, true)} disabled={busy} onClick={doList}>
              Open the Stall
            </button>
          </div>
        </Modal>
      )}

      {/* Creator gallery — "everything by this creator" */}
      {creatorView && (
        <CreatorGallery
          creatorUid={creatorView.uid}
          creatorName={creatorView.name}
          viewerUid={uid}
          onClose={() => setCreatorView(null)}
        />
      )}
    </div>
  );
}

// ── Gallery piece tile ───────────────────────────────────────────────
function GalleryPiece({ piece, onSell }) {
  const [mint, setMint] = useState(null);
  useEffect(() => {
    let alive = true;
    if (piece.artId && !piece.printId) {
      getMint(piece.artId).then((m) => alive && setMint(m));
    }
    return () => { alive = false; };
  }, [piece.artId, piece.printId]);

  const prov = PIECE_PROVENANCE[piece.provenance] || PIECE_PROVENANCE.bazaar;
  const canSell = !piece.printId; // earned prints are soulbound in v1

  return (
    <div
      style={{
        background: SD.void3,
        border: `1px solid ${prov.radiance >= 2 ? prov.color + "88" : SD.goldDim}`,
        boxShadow: prov.radiance >= 3 ? `0 0 14px ${prov.color}44` : "none",
        borderRadius: 10,
        overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ position: "relative", paddingTop: "72%", background: SD.void1 }}>
        {mint?.image && (
          <img
            src={mint.image}
            alt={mint.title}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
      </div>
      <div style={{ padding: "9px 10px", display: "flex", flexDirection: "column", gap: 5 }}>
        <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, fontSize: 13, color: SD.starWhite }}>
          {mint?.title || piece.printId || "…"}
        </div>
        {mint && (
          <div style={{ fontSize: 10, color: SD.starDim }}>
            by <span style={{ color: SD.purpleLight }}>{mint.creatorName}</span>
            {" · "}{piece.copyNo}/{mint.editionSize}
          </div>
        )}
        <SigilCode code={piece.code} />
        <ProvenanceMark provenance={piece.provenance} />
        {canSell && (
          <button style={{ ...btnStyle(SD.gold), marginTop: 4 }} onClick={onSell}>
            List for Sale
          </button>
        )}
      </div>
    </div>
  );
}

// ── MARKET PULSE — the living sales feed ─────────────────────────────
// The heartbeat of the Bazaar. A live, newest-first list of completed sales
// across the whole IE, with a stats bar (total sales, volume by currency).
// Every seller and buyer name is tappable → their creator gallery.
function timeAgo(ts) {
  const s = Math.max(0, Math.floor((Date.now() - (ts || 0)) / 1000));
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function MarketPulse({ pulse, onOpenCreator }) {
  // Volume tallies by currency across the visible feed. Built from
  // whatever actually traded rather than a fixed DC/SE pair — a new
  // title's coin starts showing a tally the first time one changes
  // hands, with nothing to update here.
  const stats = useMemo(() => {
    const vol = {};
    for (const s of pulse) {
      if (!s.currency) continue;
      vol[s.currency] = (vol[s.currency] || 0) + (Number(s.price) || 0);
    }
    return { count: pulse.length, vol };
  }, [pulse]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* stats bar */}
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
        background: SD.void3, border: `1px solid ${SD.goldDim}`, borderRadius: 10, padding: "12px 14px",
      }}>
        <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, color: SD.goldBright, letterSpacing: 2, fontSize: 13 }}>
          MARKET PULSE
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11, color: SD.starDim }}>
          <span style={{ color: SD.starWhite, fontWeight: 900 }}>{stats.count}</span> recent sales
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {Object.entries(stats.vol).map(([id, v]) => (
            <span key={id} style={{ color: tintFor(id), fontWeight: 900, fontSize: 12 }}>
              {CURRENCIES[id]?.icon || "•"} {v.toLocaleString()}
            </span>
          ))}
        </div>
      </div>

      {pulse.length === 0 ? (
        <div style={{ textAlign: "center", color: SD.starDim, padding: "60px 20px", fontSize: 13 }}>
          No sales yet — the ledger is blank parchment. The first trade written here is history.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {pulse.map((s, i) => {
            const cur = { color: tintFor(s.currency || DC_ID) };
            return (
              <div
                key={s.saleId || i}
                className="bz-anim"
                style={{
                  animation: i < 3 ? "bz-in .3s ease both" : "none",
                  display: "flex", alignItems: "center", gap: 10,
                  background: SD.void3, border: `1px solid ${SD.goldDim}`,
                  borderRadius: 8, padding: "9px 12px",
                }}
              >
                {/* a small ledger seal */}
                <div style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: cur.color, boxShadow: `0 0 6px ${cur.color}`,
                  flexShrink: 0,
                }} />
                <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: SD.starSilver, lineHeight: 1.5 }}>
                  <span style={{ color: SD.starWhite, fontWeight: 900 }}>{s.title || "A piece"}</span>
                  {" · "}
                  <button
                    onClick={() => onOpenCreator(s.seller, s.sellerName)}
                    style={linkBtn(SD.purpleLight)}
                    title="See this creator's work"
                  >{s.sellerName || "someone"}</button>
                  {" → "}
                  <button
                    onClick={() => onOpenCreator(s.buyer, s.buyerName)}
                    style={linkBtn(SD.starSilver)}
                    title="See this collector's work"
                  >{s.buyerName || "a collector"}</button>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ color: cur.color, fontWeight: 900, fontSize: 13 }}>
                    {cur.glyph} {Number(s.price).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 9, color: SD.starDim }}>{timeAgo(s.ts)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── CREATOR GALLERY — "everything by this creator" ───────────────────
// A full-screen overlay listing every edition a given uid has minted. Live
// via listenToCreatorMints; each piece shows its art, title, and edition
// size. This is the Bazaar's creator-page — reachable by tapping any name
// in the Pulse feed or on a listing card.
function CreatorGallery({ creatorUid, creatorName, viewerUid, onClose }) {
  const [mints, setMints] = useState(null); // null = loading, [] = none

  useEffect(() => {
    const off = listenToCreatorMints(creatorUid, setMints);
    return () => off && off();
  }, [creatorUid]);

  const isYou = creatorUid === viewerUid;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9650,
        background: "#000000cc",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 14,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bz-anim"
        style={{
          animation: "bz-in .25s ease both",
          width: "min(820px, 100%)", height: "min(620px, 100%)",
          background: SD.void2, border: `2px solid ${SD.purpleLight}`,
          borderRadius: 14, boxShadow: `0 0 40px ${SD.purpleLight}33`,
          display: "flex", flexDirection: "column", overflow: "hidden",
          fontFamily: SD.fontMono,
        }}
      >
        {/* header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
          borderBottom: `1px solid ${SD.purpleLight}55`, background: SD.void3,
        }}>
          <div style={{ fontSize: 20 }}>🖼️</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, fontSize: 16, color: SD.purpleLight, letterSpacing: 1 }}>
              THE WORKS OF {(creatorName || "A CREATOR").toUpperCase()}
            </div>
            <div style={{ fontSize: 10, color: SD.starDim, letterSpacing: 1 }}>
              {isYou ? "your minted editions" : "every edition minted by this creator"}
              {Array.isArray(mints) ? ` · ${mints.length}` : ""}
            </div>
          </div>
          <button style={btnStyle(SD.starSilver)} onClick={onClose}>✕</button>
        </div>

        {/* body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {mints === null ? (
            <div style={{ textAlign: "center", color: SD.starDim, padding: "60px 20px", fontSize: 13 }}>
              Reading the mint records…
            </div>
          ) : mints.length === 0 ? (
            <div style={{ textAlign: "center", color: SD.starDim, padding: "60px 20px", fontSize: 13 }}>
              {isYou
                ? "You haven't minted anything yet. Head to My Stall and strike your first edition."
                : "This creator hasn't minted any editions."}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
              {mints.map((m) => (
                <div key={m.artId} style={{
                  background: SD.void3, border: `1px solid ${SD.goldDim}`,
                  borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column",
                }}>
                  <div style={{ position: "relative", paddingTop: "72%", background: SD.void1 }}>
                    {m.image && (
                      <img src={m.image} alt={m.title}
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                    )}
                  </div>
                  <div style={{ padding: "9px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ fontFamily: SD.fontDisplay, fontWeight: 900, fontSize: 13, color: SD.starWhite }}>
                      {m.title}
                    </div>
                    <div style={{ fontSize: 10, color: SD.starDim }}>
                      edition of <span style={{ color: SD.goldBright, fontWeight: 900 }}>{m.editionSize}</span>
                    </div>
                    {m.desc && (
                      <div style={{ fontSize: 10, color: SD.starDim, lineHeight: 1.5, maxHeight: 42, overflow: "hidden" }}>
                        {m.desc}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tiny modal shell ─────────────────────────────────────────────────
function Modal({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9600,
        background: "#000000aa",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bz-anim"
        style={{
          animation: "bz-in .25s ease both",
          width: "min(440px, 100%)",
          background: SD.void2,
          border: `2px solid ${SD.gold}`,
          borderRadius: 12,
          padding: 18,
          fontFamily: SD.fontMono,
        }}
      >
        {children}
      </div>
    </div>
  );
}
